/**
 * ProfileModal — country selection and account deletion.
 *
 * Two display modes:
 *
 *   "settings"  (default) — opened from the UserPill dropdown. Shows the
 *                country picker plus, for authed users, the account-deletion
 *                section.
 *
 *   "welcome"   — auto-opened by App.tsx when a user has just signed in
 *                AND user.countryCode === "XX". Friendlier copy. Hides the
 *                account-deletion section (welcome is a hello, not a
 *                farewell). Skip-able — user can dismiss; they'll see the
 *                same picker any time from the UserPill menu.
 *
 * Country list strategy (May 10, 2026):
 *   - The picker shows the FULL ISO 3166-1 alpha-2 set (~249 entries).
 *   - When no search query is active, we render two groups in order:
 *       1. "Featured" — the 48 FIFA 2026 qualifiers + a few high-engagement
 *          non-qualifier football markets (India, etc.). Gold accent.
 *       2. "All countries" — everyone else, alphabetised.
 *   - When the user types in the search box, both groups collapse into a
 *     single flat filtered list.
 *   - The server accepts any ISO 3166-1 alpha-2 code, so `lib/countries.ts`
 *     is the single source of truth.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { COUNTRIES, UNSET_COUNTRY, flag, flagUrl, resolveCountry } from "@/lib/countries";
import { LanguagePicker } from "@/components/LanguagePicker";
import { useAuthStore } from "@/store/authStore";
import { ApiError } from "@/api/client";

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  /** "welcome" softens copy and hides delete-account; default "settings". */
  mode?: "settings" | "welcome";
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return "Country code looks wrong.";
    if (err.status === 429) return "Too many changes — try again in a minute.";
    return err.message || "Something went wrong.";
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

export function ProfileModal({ open, onClose, mode = "settings" }: ProfileModalProps) {
  const { t } = useTranslation();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const updateCountry = useAuthStore((s) => s.updateCountry);
  const deleteAccount = useAuthStore((s) => s.deleteAccount);

  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setFilter("");
      setError(null);
      setConfirmingDelete(false);
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Filter once, then split into Featured + Other groups (only when no
  // search). Searching collapses both into a single flat list so the user
  // sees results in the order they expect (alphabetical match).
  const { featured, other, flat, hasMatches } = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matches = q
      ? COUNTRIES.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            c.code.toLowerCase().includes(q),
        )
      : COUNTRIES;

    if (q) {
      return { featured: [], other: [], flat: matches, hasMatches: matches.length > 0 };
    }
    return {
      featured: matches.filter((c) => c.featured),
      other: matches.filter((c) => !c.featured),
      flat: [],
      hasMatches: matches.length > 0,
    };
  }, [filter]);

  const currentCountry = resolveCountry(user?.countryCode ?? UNSET_COUNTRY);

  if (!open) return null;

  const handlePick = async (code: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await updateCountry(code);
      onClose();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAccount();
      onClose();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const isWelcome = mode === "welcome";
  const titleText = isWelcome ? t("profile.welcome_title") : t("profile.title");
  const subtitleText = isWelcome ? t("profile.welcome_subtitle") : null;

  // Render a single country row — extracted because we use it in two
  // places (Featured group + All-countries group) and once more in the
  // flat search-result mode.
  //
  // Flag is rendered as an <img> from flagcdn.com so Windows users see
  // an actual flag (regional-indicator emoji are letters-in-boxes on
  // Windows). loading="lazy" means a 250-row dropdown only fetches the
  // ~12 visible flags initially.
  const renderRow = (c: (typeof COUNTRIES)[number]) => {
    const isCurrent = c.code === currentCountry.code;
    const url = flagUrl(c.code, 40);
    return (
      <button
        key={c.code}
        type="button"
        disabled={busy || isCurrent}
        onClick={() => handlePick(c.code)}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition ${
          isCurrent
            ? "bg-emerald-700/40 ring-1 ring-emerald-300/50"
            : c.featured
              ? "hover:bg-amber-300/10"
              : "hover:bg-white/10"
        }`}
      >
        {url ? (
          <img
            src={url}
            width={20}
            height={15}
            loading="lazy"
            decoding="async"
            alt=""
            className="rounded-sm shadow-[0_0_0_1px_rgba(255,255,255,0.1)]"
          />
        ) : (
          <span className="text-base" aria-hidden="true">🌐</span>
        )}
        <span className={`flex-1 ${c.featured ? "font-medium" : ""}`}>{c.name}</span>
        <span className="text-xs opacity-50">{c.code}</span>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-title"
      onClick={onClose}
    >
      <div
        className="w-[min(28rem,90%)] max-h-[85vh] flex flex-col rounded-3xl bg-emerald-950/95 p-6 text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 id="profile-title" className="text-xl font-bold">
            {titleText}
          </h2>
          <button
            type="button"
            aria-label={isWelcome ? t("profile.skip") : t("profile.close")}
            className="opacity-60 hover:opacity-100"
            onClick={onClose}
          >
            {isWelcome ? t("profile.skip") : "✕"}
          </button>
        </div>

        {subtitleText ? (
          <p className="mt-2 text-sm opacity-80">{subtitleText}</p>
        ) : (
          <>
            <p className="mt-2 flex items-center gap-2 text-sm opacity-75">
              <span>{t("profile.country_label")}</span>
              {flagUrl(currentCountry.code, 40) ? (
                <img
                  src={flagUrl(currentCountry.code, 40)!}
                  width={20}
                  height={15}
                  alt=""
                  className="rounded-sm shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
                />
              ) : (
                <span aria-hidden="true">{flag(currentCountry.code)}</span>
              )}
              <span className="font-semibold">{currentCountry.name}</span>
            </p>
            <p className="text-xs opacity-50">
              {t("profile.country_help")}
            </p>
          </>
        )}

        {/* Language picker — appears in both welcome and settings modes so
            users can switch language at first touch. Choice persists across
            sessions via localStorage. */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs opacity-70">
          <span>{t("profile.language_label")}:</span>
          <LanguagePicker compact />
        </div>

        <input
          ref={inputRef}
          type="text"
          placeholder={t("profile.search_placeholder")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mt-4 rounded-xl bg-white/10 px-3 py-2 text-base outline-none ring-emerald-400/60 focus:ring-2"
        />

        <div className="mt-3 flex-1 overflow-y-auto rounded-xl bg-black/20 p-1">
          {!hasMatches ? (
            <p className="p-3 text-sm opacity-60">{t("profile.no_matches")}</p>
          ) : filter.trim() ? (
            // Search mode: flat list.
            flat.map(renderRow)
          ) : (
            <>
              {featured.length > 0 && (
                <>
                  <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-amber-200/80">
                    {t("profile.featured_section")}
                  </p>
                  {featured.map(renderRow)}
                </>
              )}
              {other.length > 0 && (
                <>
                  <p className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider opacity-50">
                    {t("profile.all_countries_section")}
                  </p>
                  {other.map(renderRow)}
                </>
              )}
            </>
          )}
        </div>

        {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}

        {/* Account-deletion section: only in settings mode for authed users.
            Welcome mode hides it — first impression should not include a
            "delete account" button. */}
        {!isWelcome && status === "authed" && (
          <div className="mt-5 border-t border-white/10 pt-4">
            {!confirmingDelete ? (
              <button
                type="button"
                disabled={busy}
                className="text-xs text-rose-300/80 underline hover:text-rose-300"
                onClick={() => setConfirmingDelete(true)}
              >
                {t("profile.delete_account")}
              </button>
            ) : (
              <div className="flex flex-col gap-3 rounded-xl bg-rose-900/30 p-3 ring-1 ring-rose-500/40">
                <p className="text-xs text-rose-100">
                  {t("profile.delete_confirm")}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    className="rounded-full bg-rose-600 px-4 py-1.5 text-xs font-semibold transition hover:bg-rose-500 disabled:opacity-50"
                    onClick={handleDelete}
                  >
                    {busy ? t("profile.delete_deleting") : t("profile.delete_yes")}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    className="rounded-full bg-white/10 px-4 py-1.5 text-xs hover:bg-white/20"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    {t("profile.cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
